import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { notificationsAPI } from '../services/api';
import { Bell, Check, Clock, CalendarDays, AlertCircle } from 'lucide-react';

export default function NotificationBell() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (user?.user_id) {
      loadUnreadCount();
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.user_id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const data = await notificationsAPI.getAll(true);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationsAPI.getAll();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen) loadNotifications();
    setIsOpen(!isOpen);
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await notificationsAPI.markRead(notificationId);
      setNotifications(prev => prev.map(n =>
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'schedule': return <CalendarDays className="w-5 h-5 text-blue-500" />;
      case 'ot_request': return <Clock className="w-5 h-5 text-orange-500" />;
      case 'ot_result': return <AlertCircle className="w-5 h-5 text-blue-500" />;
      case 'ot_decision': return <Check className="w-5 h-5 text-green-500" />;
      case 'leave_request': return <CalendarDays className="w-5 h-5 text-purple-500" />;
      case 'leave_decision': return <Check className="w-5 h-5 text-emerald-500" />;
      case 'payslip_request': return <Bell className="w-5 h-5 text-amber-500" />;
      case 'geofence_alert': return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'geofence_warning': return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'auto_clock_out': return <Clock className="w-5 h-5 text-red-600" />;
      case 'carry_hour': return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'daily_summary': return <Bell className="w-5 h-5 text-blue-600" />;
      default: return <Bell className="w-5 h-5 text-stone-500" />;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000 / 60;
    if (diff < 1) return 'Baru';
    if (diff < 60) return `${Math.floor(diff)}m lalu`;
    if (diff < 1440) return `${Math.floor(diff / 60)}j lalu`;
    return date.toLocaleDateString('ms-MY', { day: '2-digit', month: 'short' });
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        data-testid="notification-bell"
        className="relative p-2 rounded-xl hover:bg-orange-50 dark:hover:bg-stone-800 transition-colors"
      >
        <Bell className="w-5 h-5 text-stone-600 dark:text-stone-400" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="fixed sm:absolute right-2 sm:right-0 top-14 sm:top-auto sm:mt-2 w-[calc(100vw-16px)] sm:w-96 max-h-[70vh] overflow-hidden bg-white dark:bg-stone-800 rounded-2xl shadow-xl border border-stone-200 dark:border-stone-700 z-50" data-testid="notification-dropdown">
          <div className="p-4 border-b border-stone-200 dark:border-stone-700 flex items-center justify-between">
            <h3 className="font-semibold text-stone-800 dark:text-stone-100">Notifikasi</h3>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} className="text-xs text-orange-600 hover:text-orange-700 dark:text-orange-400">
                Tandai semua dibaca
              </button>
            )}
          </div>

          <div className="overflow-y-auto max-h-[50vh]">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 text-stone-300 dark:text-stone-600 mx-auto mb-2" />
                <p className="text-stone-500 dark:text-stone-400 text-sm">Tiada notifikasi</p>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.notification_id}
                  onClick={() => !notification.read && handleMarkRead(notification.notification_id)}
                  data-testid={`notification-item-${notification.notification_id}`}
                  className={`p-4 border-b border-stone-100 dark:border-stone-700 cursor-pointer hover:bg-stone-50 dark:hover:bg-stone-700/50 transition-colors ${
                    !notification.read ? 'bg-orange-50/50 dark:bg-orange-900/10' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!notification.read ? 'font-semibold' : ''} text-stone-800 dark:text-stone-100`}>
                          {notification.title}
                        </p>
                        <span className="text-xs text-stone-400 whitespace-nowrap">
                          {formatTime(notification.created_at)}
                        </span>
                      </div>
                      <p className="text-xs text-stone-500 dark:text-stone-400 mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      {!notification.read && (
                        <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mt-2"></span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
